import {useEffect, useMemo} from 'react';
import {useQuery} from '@apollo/client';
import {Partner} from '@/Pages/Partners/store/types';
import {VehicleTypeCostEntry} from '@/Pages/ProductionCost/store/types';
import {receivedVehicleTypeCostEntries} from '@/Pages/ProductionCost/store/slice';
import {useAppSelector} from '@/Pages/Common/hooks/useAppSelector';
import {useAppDispatch} from '@/Pages/Common/hooks/useAppDispatch';
import {selectVehicleTypeCostEntriesById} from '@/Pages/ProductionCost/store/customSelectors';
import getBusPartnerAndCountryCostEntriesQuery from '@/Pages/ProductionCost/graphql/getBusPartnerAndCountryCostEntriesQuery';

export interface GetVehicleTypeCostEntriesResponse {
  getBusPartnerCostEntryYear: {
    busTypeCostEntries: VehicleTypeCostEntry[];
  } | null;
  countryCostEntry: {
    busTypeCostEntries: VehicleTypeCostEntry[];
  } | null;
}

const useVehicleTypeCostEntries = (busPartner: Partner | null) => {
  const year = useAppSelector(({productionCost}) => productionCost.costYear);
  const dispatch = useAppDispatch();

  const id = useMemo(() => {
    if (!busPartner || !year) return null;
    return `${busPartner.id}${busPartner.countryId}${year}`;
  }, [busPartner, year]);

  const vehicleTypeCostEntries = useAppSelector(({productionCost}) =>
    selectVehicleTypeCostEntriesById(productionCost, {id})
  );

  const {data, loading, error} = useQuery<GetVehicleTypeCostEntriesResponse>(
    getBusPartnerAndCountryCostEntriesQuery,
    {
      skip: !busPartner || !year,
      variables:
        busPartner && year
          ? {
              countryId: busPartner.countryId,
              busPartnerId: busPartner.id,
              year
            }
          : undefined,
      errorPolicy: 'all'
    }
  );

  useEffect(() => {
    if (!id || !data) return;

    dispatch(
      receivedVehicleTypeCostEntries({
        id,
        vehicleTypeCostEntries: {
          busPartnerBusTypeCostEntries:
            data.getBusPartnerCostEntryYear?.busTypeCostEntries ?? [],
          countryBusTypeCostEntries:
            data.countryCostEntry?.busTypeCostEntries ?? []
        }
      })
    );
  }, [data, dispatch, id]);

  useEffect(() => {
    if (error) {
      const unexpectedErrors = error.graphQLErrors.filter(
        error => error.extensions?.errorType !== 'ResourceNotFound'
      );
      if (unexpectedErrors.length) {
        unexpectedErrors.forEach(e => {
          console.error('Error fetching vehicle type cost entries: ', e);
        });
      }
    }
  }, [error]);

  return {
    vehicleTypeCostEntries,
    loading,
    error
  };
};

export default useVehicleTypeCostEntries;
